import { Bookmark, Building2, Check, ChevronDown, Pencil, Plus, RefreshCw, Trash2, Users } from "lucide-mui";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";
import MenuItem from "@mui/material/MenuItem";
import MenuList from "@mui/material/MenuList";
import Popover from "@mui/material/Popover";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import toast from "react-hot-toast";
import { api, type SavedView, type SavedViewVisibility } from "../../api/client.ts";
import { keys } from "../../api/queryKeys.ts";
import ConfirmDialog from "../ConfirmDialog.tsx";
import SaveViewDialog, { type ViewContext } from "./SaveViewDialog.tsx";
import type { FilterParams, Filters } from "./useFilters.ts";

const canonical = (p: FilterParams) => JSON.stringify(Object.keys(p).sort().map((k) => [k, p[k]]));

interface SavedViewsMenuProps {
  filters: Filters;
  context: ViewContext;
}

/**
 * The page's saved views: apply one, save the current filters as a new one
 * (private or shared with the tenant / org), update, rename or delete.
 */
export default function SavedViewsMenu({ filters, context }: SavedViewsMenuProps) {
  const pageKey = filters.pageKey ?? "";
  const queryClient = useQueryClient();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [dialog, setDialog] = useState<{ edit?: SavedView } | null>(null);
  const [deleting, setDeleting] = useState<SavedView | null>(null);
  const tenantId = context.tenantId ?? null;
  const orgId = context.organizationId ?? null;

  const { data: views = [] } = useQuery({
    queryKey: keys.savedView.list(pageKey, tenantId, orgId),
    queryFn: () =>
      api.getSavedViews({
        page_key: pageKey,
        tenant_id: tenantId ?? undefined,
        organization_id: orgId ?? undefined,
      }),
    enabled: !!pageKey,
    staleTime: 60_000,
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: keys.savedView.all });

  const current = canonical(filters.params);
  const active = views.find((v) => canonical(v.params) === current);
  // The view last applied, while its filters have been changed since.
  const [base, setBase] = useState<string | null>(null);
  const modified = !active ? views.find((v) => v.id === base && v.is_owner) : undefined;

  const create = useMutation({
    mutationFn: ({ name, visibility }: { name: string; visibility: SavedViewVisibility }) =>
      api.createSavedView({
        page_key: pageKey,
        name,
        visibility,
        params: filters.params,
        tenant_id: tenantId,
        organization_id: orgId,
      }),
    onSuccess: (view) => {
      refresh();
      setBase(view.id);
      setDialog(null);
      toast.success(`Saved "${view.name}"`);
    },
  });
  const update = useMutation({
    mutationFn: ({ id, ...data }: { id: string; name?: string; visibility?: SavedViewVisibility; params?: FilterParams }) =>
      api.updateSavedView(id, data),
    onSuccess: (view) => {
      refresh();
      setDialog(null);
      toast.success(`Updated "${view.name}"`);
    },
  });
  const remove = useMutation({
    mutationFn: (view: SavedView) => api.deleteSavedView(view.id),
    onSuccess: () => {
      refresh();
      setDeleting(null);
      toast.success("View deleted");
    },
  });

  const apply = (view: SavedView) => {
    filters.applyParams(view.params);
    setBase(view.id);
    setAnchor(null);
  };

  const mine = views.filter((v) => v.visibility === "private" && v.is_owner);
  const shared = views.filter((v) => v.visibility !== "private");
  const label = active?.name ?? (modified ? `${modified.name} (edited)` : "Views");

  const row = (v: SavedView) => (
    <MenuItem
      key={v.id}
      selected={active?.id === v.id}
      onClick={() => apply(v)}
      sx={{ "&:hover .view-actions, &.Mui-focusVisible .view-actions": { opacity: 1 } }}
    >
      <ListItemIcon sx={{ minWidth: 16 }}>{active?.id === v.id && <Check />}</ListItemIcon>
      <ListItemText
        primary={v.name}
        secondary={
          v.visibility === "private"
            ? undefined
            : `${v.visibility === "tenant" ? context.tenantName ?? "Tenant" : context.organizationName ?? "Organization"}${
                v.is_owner ? "" : ` · ${v.owner_display_name || v.owner_email}`
              }`
        }
        slotProps={{ primary: { noWrap: true }, secondary: { noWrap: true } }}
      />
      {v.visibility === "tenant" && <Users sx={{ fontSize: 14, color: "text.secondary" }} />}
      {v.visibility === "organization" && <Building2 sx={{ fontSize: 14, color: "text.secondary" }} />}
      <Box className="view-actions" sx={{ display: "flex", opacity: 0, transition: "opacity 120ms" }}>
        {v.is_owner && (
          <Tooltip title="Rename or share">
            <IconButton
              size="small"
              aria-label={`Edit view ${v.name}`}
              onClick={(e) => {
                e.stopPropagation();
                setAnchor(null);
                setDialog({ edit: v });
              }}
            >
              <Pencil sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        )}
        {v.can_delete && (
          <Tooltip title="Delete">
            <IconButton
              size="small"
              aria-label={`Delete view ${v.name}`}
              onClick={(e) => {
                e.stopPropagation();
                setAnchor(null);
                setDeleting(v);
              }}
            >
              <Trash2 sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </MenuItem>
  );

  return (
    <>
      <Button
        size="small"
        color="neutral"
        startIcon={<Bookmark />}
        endIcon={<ChevronDown sx={{ fontSize: 14 }} />}
        onClick={(e) => setAnchor(e.currentTarget)}
        aria-haspopup="menu"
        sx={{
          ...((active || modified) && { color: "text.primary" }),
          maxWidth: 220,
          "& .MuiButton-startIcon": { color: active ? "primary.main" : "inherit" },
        }}
      >
        <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {label}
        </Box>
      </Button>
      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { mt: 0.5, width: 300 } } }}
      >
        {/* Flat children: a MenuList walks its direct children for arrow-key focus. */}
        <MenuList sx={{ maxHeight: 480, overflowY: "auto" }}>
          {views.length === 0 && (
            <Typography variant="body2" sx={{ color: "text.secondary", px: 1.75, py: 1 }}>
              Save the filters you use often, for yourself or your team.
            </Typography>
          )}
          {mine.length > 0 && <ListSubheader>My views</ListSubheader>}
          {mine.map(row)}
          {shared.length > 0 && <ListSubheader>Shared</ListSubheader>}
          {shared.map(row)}
          <Divider />
          {modified && (
            <MenuItem
              onClick={() => {
                setAnchor(null);
                update.mutate({ id: modified.id, params: filters.params });
              }}
            >
              <ListItemIcon>
                <RefreshCw />
              </ListItemIcon>
              <ListItemText primary={`Update “${modified.name}”`} slotProps={{ primary: { noWrap: true } }} />
            </MenuItem>
          )}
          <MenuItem
            disabled={!!active || Object.keys(filters.params).length === 0}
            onClick={() => {
              setAnchor(null);
              setDialog({});
            }}
          >
            <ListItemIcon>
              <Plus />
            </ListItemIcon>
            <ListItemText primary="Save current filters…" />
          </MenuItem>
        </MenuList>
      </Popover>
      {dialog && (
        <SaveViewDialog
          open
          initial={dialog.edit && { name: dialog.edit.name, visibility: dialog.edit.visibility }}
          context={
            dialog.edit
              ? // A view's context is fixed: offer its own tenant / org, not the page's.
                {
                  tenantId: dialog.edit.tenant_id,
                  tenantName: dialog.edit.tenant_id === tenantId ? context.tenantName : "its tenant",
                  organizationId: dialog.edit.organization_id,
                  organizationName:
                    dialog.edit.organization_id === orgId ? context.organizationName : "its organization",
                }
              : context
          }
          pending={create.isPending || update.isPending}
          onSave={(name, visibility) =>
            dialog.edit ? update.mutate({ id: dialog.edit.id, name, visibility }) : create.mutate({ name, visibility })
          }
          onClose={() => setDialog(null)}
        />
      )}
      <ConfirmDialog
        open={!!deleting}
        title="Delete view"
        pending={remove.isPending}
        onConfirm={() => deleting && remove.mutate(deleting)}
        onClose={() => setDeleting(null)}
      >
        Delete <strong>{deleting?.name}</strong>
        {deleting && deleting.visibility !== "private" ? "? Everyone it's shared with loses it." : "?"}
      </ConfirmDialog>
    </>
  );
}
